import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  Globe,
  TrendingUp,
  ExternalLink,
  Linkedin,
  Calculator
} from 'lucide-react'
import ValuationIntelligence from '../components/features/ValuationIntelligence'
import ROIAnalysis from '../components/features/ROIAnalysis'

export default function CompanyDetail() {
  const { companyId } = useParams()
  const navigate = useNavigate()
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('valuation')
  const [valuationData, setValuationData] = useState(null)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)

  useEffect(() => {
    loadCompany()
  }, [companyId])

  const loadCompany = () => {
    setLoading(true)
    setError(null)

    try {
      const storedCompany = sessionStorage.getItem('selectedCompany')

      if (storedCompany) {
        const parsed = JSON.parse(storedCompany)
        if (parsed.profileId === companyId) {
          setCompany(parsed)
          setLoading(false)
          return
        }
      }

      setError('Company data not found. Please select a company from the list.')
      setCompany(null)
    } catch (err) {
      console.error('Failed to load company:', err)
      setError('Failed to load company data')
      setCompany(null)
    } finally {
      setLoading(false)
    }
  }

  const formatRevenue = (revenue) => {
    if (!revenue) return 'N/A'
    if (revenue >= 1000) return `$${(revenue / 1000).toFixed(1)}B`
    if (revenue < 1) return `$${(revenue * 1000).toFixed(0)}k`
    return `$${revenue.toFixed(1)}M`
  }

  // Merge capabilities and expertCapabilities
  const getAllCapabilities = () => {
    const caps = new Set()

    if (company?.capabilities) {
      company.capabilities.forEach(c => caps.add(typeof c === 'string' ? c : c.name))
    }

    if (company?.expertCapabilities) {
      company.expertCapabilities.forEach(c => caps.add(typeof c === 'string' ? c : c.name))
    }

    return Array.from(caps)
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-neutral-200 rounded w-48" />
          <div className="h-64 bg-neutral-200 rounded" />
        </div>
      </div>
    )
  }

  if (!company) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate('/companies')}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Mandates
        </button>
        <div className="bg-white rounded-lg border border-neutral-200 p-12 text-center">
          <Building2 className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
          <p className="text-neutral-500 mb-4">{error || 'Company not found'}</p>
          <button
            onClick={() => navigate('/companies')}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600 transition-colors"
          >
            Go to Mandates
          </button>
        </div>
      </div>
    )
  }

  const capabilities = getAllCapabilities()

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm mb-6">
        <button
          onClick={() => navigate('/companies')}
          className="text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          Mandates
        </button>
        <span className="text-neutral-300">/</span>
        {company.mandateName && (
          <>
            <button
              onClick={() => navigate(`/companies?mandate=${company.mandateId}`)}
              className="text-neutral-500 hover:text-neutral-900 transition-colors"
            >
              {company.mandateName}
            </button>
            <span className="text-neutral-300">/</span>
          </>
        )}
        <span className="text-neutral-900 font-medium">{company.name}</span>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          {error}
        </div>
      )}

      {/* Company Header Card */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-6">
        <div className="flex items-start gap-4">
          {/* Logo */}
          <div className="w-14 h-14 bg-neutral-100 rounded-lg flex items-center justify-center shrink-0">
            {company.logoUrl ? (
              <img
                src={company.logoUrl}
                alt={company.name}
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none'
                  e.target.nextElementSibling.style.display = 'flex'
                }}
              />
            ) : null}
            <Building2 className={`w-7 h-7 text-neutral-400 ${company.logoUrl ? 'hidden' : ''}`} />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-neutral-900">{company.name}</h1>
                {company.website && (
                  <a
                    href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 hover:text-primary-600"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                {company.linkedin && (
                  <a
                    href={company.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 hover:text-primary-600"
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                )}
              </div>
              {company.sector && (
                <span className="px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-md shrink-0 h-fit">
                  {company.sector}
                </span>
              )}
            </div>

            {/* Description - full width below the header row */}
            {company.description && (
              <div className="mt-2">
                <p className={`text-neutral-600 text-sm ${descriptionExpanded ? '' : 'line-clamp-2'}`}>
                  {company.description}
                </p>
                {company.description.length > 100 && (
                  <button
                    onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium mt-1"
                  >
                    {descriptionExpanded ? 'Show less' : 'Read more'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-neutral-100">
          <div className="min-w-0">
            <p className="text-xs text-neutral-500">Revenue</p>
            <p className="text-sm font-semibold text-neutral-900 mt-0.5">
              {formatRevenue(company.revenue)}
            </p>
          </div>
          <div className="min-w-0">
            <p className="text-xs text-neutral-500">Location</p>
            <p className="text-sm font-semibold text-neutral-900 mt-0.5 truncate" title={company.headquarters || 'N/A'}>
              {company.headquarters || 'N/A'}
            </p>
          </div>
          <div className="min-w-0">
            <p className="text-xs text-neutral-500">Employees</p>
            <p className="text-sm font-semibold text-neutral-900 mt-0.5">
              {company.employeeRange || company.employees || 'N/A'}
            </p>
          </div>
          <div className="min-w-0">
            <p className="text-xs text-neutral-500">Founded</p>
            <p className="text-sm font-semibold text-neutral-900 mt-0.5">
              {company.foundingYear || 'N/A'}
            </p>
          </div>
        </div>

        {/* Key Offerings - Top 3 only */}
        {capabilities.length > 0 && (
          <div className="mt-6 pt-6 border-t border-neutral-100">
            <p className="text-sm font-medium text-neutral-700 mb-3">Key Offerings</p>
            <div className="flex flex-wrap gap-2">
              {capabilities.slice(0, 3).map((cap, idx) => (
                <span key={idx} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-md">
                  {cap}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Industries */}
        {company.industries && company.industries.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-neutral-700 mb-3">Industries Served</p>
            <div className="flex flex-wrap gap-2">
              {company.industries.map((ind, idx) => (
                <span key={idx} className="px-3 py-1 bg-neutral-100 text-neutral-600 text-sm rounded-md">
                  {ind}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-neutral-200">
        <button
          onClick={() => setActiveTab('valuation')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'valuation'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          Valuation Intelligence
        </button>
        <button
          onClick={() => setActiveTab('roi')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'roi'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          <Calculator className="w-4 h-4" />
          ROI Calculator
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'valuation' ? (
        <ValuationIntelligence target={company} onValuationChange={setValuationData} />
      ) : (
        <ROIAnalysis target={company} valuationData={valuationData} />
      )}
    </div>
  )
}
